import {
  Combobox,
  DropdownButton,
  NoOptionsPlaceholder
} from '@eggplant/pattern-library'
import type {
  ButtonShape,
  DropdownPlacement,
  GetGitConnectionsResponse,
  Options,
  Placement
} from '@eggplant/types'
import {
  ALL_INTERNAL_STORAGE,
  ALL_GIT,
  defaultResponse,
  initialOptions
} from './constants'
import { HiOutlineFolder } from 'react-icons/hi'
import {
  LinkToAddGitConnection,
  useSpaceAccess
} from '@eggplant/routing'
import { FiPlus } from 'react-icons/fi'
import { GetGitConnectionsRequest } from '@eggplant/api-requests'
import { useState } from 'react'

interface Props {
  onChange: (value: string) => void
  placeholder?: string
  value?: string
  placement?: Placement
  dropdownPlacement?: DropdownPlacement
  isFilterFixedMinMaxWidth?: boolean
  isFilter?: boolean
}

interface labelValue {
  label: string
  value: string
}

const getOptions = ({ items }: GetGitConnectionsResponse) => items.length > 0
  ? items.map(item => ({
    label: item.name,
    value: item.id
  }) as labelValue)
  : []

const SelectGitRepo = ({
  onChange,
  placeholder = 'Repository',
  value,
  dropdownPlacement = 'bottom',
  placement,
  isFilterFixedMinMaxWidth = true,
  isFilter = false
}: Props) => {
  const [isAccessible] = useSpaceAccess()
  const [gitConnections, setGitConnections] = useState<GetGitConnectionsResponse>(defaultResponse)

  const options = getOptions(gitConnections)

  // eslint-disable-next-line complexity
  const renderButton = (options: Options) => {
    const currentOption = value
      ? options.find(item => item.value.includes(value))
      : undefined

    const label = currentOption?.value
      ? currentOption.label
      : placeholder

    const repositoryTooltip = [ALL_INTERNAL_STORAGE, ALL_GIT].includes(String(value))
      ? label
      : `Repository: ${label}`

    const tooltip = currentOption?.value
      ? repositoryTooltip
      : placeholder

    const buttonText = currentOption?.value
      ? currentOption.label
      : placeholder

    return (buttonProps: ButtonShape) =>
      <DropdownButton
        {...buttonProps}
        Icon={HiOutlineFolder}
        dropdownPlacement={dropdownPlacement}
        isFilter={isFilter}
        isFilterFixedMinMaxWidth={isFilterFixedMinMaxWidth}
        isHighlighted={!!value}
        label={label}
        placement={placement}
        title={tooltip}
      >
        {buttonText}
      </DropdownButton>
  }

  const noDataPlaceholder =
    <NoOptionsPlaceholder
      icon={<HiOutlineFolder size='24px' />}
      linkTo={
        isAccessible
          ? (
            <LinkToAddGitConnection>
              <FiPlus
                aria-hidden
                size={16}
              />
              New Git Connection
            </LinkToAddGitConnection>
          )
          : undefined
      }
      title='No Git connection found'
    />

  const handleOnSuccess = (data: GetGitConnectionsResponse) => {
    setGitConnections(data)
  }

  return (
    <GetGitConnectionsRequest
      onSuccess={handleOnSuccess}
      shortErrorMessage='Failed to load git connections'
    >
      {
        () =>
          <Combobox
            dropdownPlacement={dropdownPlacement}
            groupedOptions={
              [{
                label: 'Git connections',
                options
              }]
            }
            initialOptions={initialOptions}
            isFilter={isFilter}
            isFilterFixedMinMaxWidth={isFilterFixedMinMaxWidth}
            noOptionsPlaceholder={noDataPlaceholder}
            onChange={onChange}
            placement={placement}
            renderButton={renderButton(options)}
            value={value}
          />
      }
    </GetGitConnectionsRequest>
  )
}

export default SelectGitRepo

